import type { ReactNode } from "react";
import { ComplianceStatusBadge } from "./compliance-status-badge";
import { VerbatimErrorText } from "./verbatim-error-text";

/**
 * Where an invoice stands with e-invoice registration (`011` FR-010, FR-013, R6).
 *
 * ## Five states, and collapsing any pair loses information a compliance user needs
 *
 * | | means |
 * |---|---|
 * | `not_applicable` | below a configured threshold. **Nothing is missing** (D-24, FR-013) |
 * | `pending` | in flight, **or the far end could not be reached**. Never rendered as failed (FR-016) |
 * | `registered` | an IRN was obtained; printing goes through `006`'s path (FR-014) |
 * | `failed` | the portal answered and refused. The reason is rendered verbatim (FR-011) |
 * | `cancelled` | the IRN was cancelled and its outcome stated (FR-017) |
 *
 * `not_applicable` is the one people get wrong. It is the correct and complete answer for most
 * invoices under most thresholds, so it must not read as an omission — no dash, no empty cell, no
 * "none". It says so in words.
 *
 * ## No countdown, no threshold, no portal vocabulary
 *
 * `window` is the API's own wording about the remaining cancellation window (FR-019): a countdown
 * computed against the client's clock offers a cancellation that will be refused, and a skewed clock
 * at a loading bay is not hypothetical. No threshold figure appears here (I-09, FR-025), and no
 * portal, GSP or field name (I-08, FR-024) — `reason` is data, not vocabulary.
 */
export type RegistrationStateName =
  | "not_applicable"
  | "pending"
  | "registered"
  | "failed"
  | "cancelled";

const SAYS: Record<RegistrationStateName, string> = {
  not_applicable:
    "This invoice is below the configured threshold for e-invoice registration. Nothing is missing.",
  pending: "Registration is in progress, or the service could not be reached. Nothing has failed.",
  registered: "This invoice is registered.",
  failed: "Registration was refused. The reason is below, exactly as it was returned.",
  cancelled: "The registration for this invoice was cancelled.",
};

export function RegistrationState({
  state,
  irn,
  reason,
  window: cancellationWindow,
  children,
}: {
  state: RegistrationStateName;
  /** The IRN, when there is one. Never a placeholder when there is not (FR-012). */
  irn?: string;
  /** The service's refusal, verbatim. Rendered only on `failed`. */
  reason?: string;
  /** The API's wording for the remaining cancellation window. Never computed here (FR-019). */
  window?: ReactNode;
  /** A deliberate retry, or the route to `006`'s credit note when the window has passed (FR-018). */
  children?: ReactNode;
}) {
  return (
    <section
      data-slot="registration-state"
      data-state={state}
      aria-label="E-invoice registration"
      className="border-muted bg-gray-0 flex flex-col gap-3 rounded-xl border p-4 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ComplianceStatusBadge
          subject="irn"
          state={
            state === "registered"
              ? "done"
              : state === "cancelled"
                ? "expired"
                : (state as "not_applicable" | "pending" | "failed")
          }
        />
        {cancellationWindow ? (
          <span className="text-muted-foreground text-xs">{cancellationWindow}</span>
        ) : null}
      </div>

      <p className="text-muted-foreground">{SAYS[state]}</p>

      {irn ? (
        <p className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            IRN
          </span>
          <span className="font-mono text-xs break-all">{irn}</span>
        </p>
      ) : null}

      {state === "failed" && reason ? (
        <VerbatimErrorText label="The registration service's response">{reason}</VerbatimErrorText>
      ) : null}

      {children ? <div className="pt-0.5">{children}</div> : null}
    </section>
  );
}
